import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    CircularProgress,
    Alert,
    Divider,
    Stack,
} from "@mui/material";
import {
    CheckCircleOutline as AccountabilityIcon,
    EventSeat as SeatIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import LeadershipService from "../services/Leadership.service";

export default function Accountabilities() {
    const { user } = useAuth();
    const [seats, setSeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user?.employee_id) {
            setLoading(false);
            return;
        }
        LeadershipService.seats(user.employee_id)
            .then((res) => setSeats(res.data))
            .catch(() => setError("Failed to load accountabilities."))
            .finally(() => setLoading(false));
    }, [user?.employee_id]);

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AccountabilityIcon sx={{ fontSize: 36, color: "secondary.main" }} />
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                        My Accountabilities
                    </Typography>
                </Stack>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : seats.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <SeatIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        You haven't been placed in a seat on the Accountability Chart yet.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {seats.map((seat) => {
                        const others = (seat.people || []).filter(
                            (p) => p.id !== user.employee_id,
                        );
                        return (
                            <Paper
                                key={seat.id}
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <SeatIcon color="primary" />
                                        <Typography
                                            fontWeight={700}
                                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                                        >
                                            {seat.title || "Untitled seat"}
                                        </Typography>
                                    </Stack>
                                    {others.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5, ml: 4 }}
                                        >
                                            Shared with {others.map((p) => p.name).join(", ")}
                                        </Typography>
                                    )}
                                </Box>
                                <Divider />
                                {seat.accountabilities.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ px: 2, py: 1.5, fontStyle: "italic" }}
                                    >
                                        No accountabilities defined for this seat yet.
                                    </Typography>
                                ) : (
                                    <List disablePadding>
                                        {seat.accountabilities.map((item, index) => (
                                            <React.Fragment key={item.id}>
                                                {index > 0 && <Divider />}
                                                <ListItem sx={{ px: 2, py: 1.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        <AccountabilityIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.description}
                                                        primaryTypographyProps={{
                                                            fontFamily: "Montserrat, sans-serif",
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </ListItem>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}
